/** @jsx jsx */
import { jsx } from 'theme-ui'
import React, { useEffect, useState, useCallback } from 'react'

/**
 * Cookie consent banner + preferences dialog.
 *
 * - Google Consent Mode v2 defaults (all non-essential storage denied) are set in
 *   gatsby-ssr.js BEFORE the gtag script loads; this component only *updates* consent.
 * - The choice is persisted in localStorage under CONSENT_KEY (12 months) and mirrored in
 *   a first-party cookie so the server-rendered page can skip the banner on return visits.
 * - Reopen the dialog from anywhere with `window.dispatchEvent(new Event('sparkey:cookie-settings'))`
 *   or by visiting any page with the `#cookie-settings` hash (footer "Cookie settings" link).
 */

export const CONSENT_KEY = 'sparkey-cookie-consent'
export const CONSENT_VERSION = 1
const MAX_AGE_DAYS = 365

const CATEGORIES = [
  {
    id: 'necessary',
    label: 'Strictly necessary',
    locked: true,
    description:
      'Required for the site to function (session, security and this consent record). Always on.'
  },
  {
    id: 'analytics',
    label: 'Analytics',
    locked: false,
    description:
      'Google Analytics 4 with IP anonymisation — helps us understand how visitors use sparkey.ai. No cross-site tracking or advertising.'
  },
  {
    id: 'functional',
    label: 'Functional',
    locked: false,
    description: 'Remembers preferences such as your theme choice.'
  }
]

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed.version !== CONSENT_VERSION) return null
    if (Date.now() - parsed.at > MAX_AGE_DAYS * 864e5) return null
    return parsed
  } catch (e) {
    return null
  }
}

const writeStored = choices => {
  const record = { version: CONSENT_VERSION, at: Date.now(), choices }
  try {
    window.localStorage.setItem(CONSENT_KEY, JSON.stringify(record))
    document.cookie = `${CONSENT_KEY}=${choices.analytics ? 'analytics' : 'necessary'}; Max-Age=${
      MAX_AGE_DAYS * 86400
    }; Path=/; SameSite=Lax; Secure`
  } catch (e) {
    /* storage unavailable — consent stays session-only */
  }
  return record
}

export const applyConsent = choices => {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  const gtag = function () {
    window.dataLayer.push(arguments)
  }
  gtag('consent', 'update', {
    analytics_storage: choices.analytics ? 'granted' : 'denied',
    functionality_storage: choices.functional ? 'granted' : 'denied',
    personalization_storage: choices.functional ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    security_storage: 'granted'
  })
}

const ALL_ON = { necessary: true, analytics: true, functional: true }
const ALL_OFF = { necessary: true, analytics: false, functional: false }

const styles = {
  // One-line bottom bar. Colors and sizing are explicit (no theme vars) so the bar
  // is pixel-identical in dark and light themes; border-box keeps it inside the viewport.
  banner: {
    position: 'fixed',
    bottom: '1rem',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'min(1040px, calc(100vw - 1.5rem))',
    boxSizing: 'border-box',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    flexWrap: ['wrap', 'nowrap'],
    gap: ['0.5rem', '1rem'],
    background: '#14141d',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '12px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
    px: '1rem',
    py: '0.55rem',
    color: '#d4d6dd',
    fontSize: '0.85rem',
    lineHeight: 1.45,
    '[data-theme="light"] &': {
      background: '#ffffff',
      border: '1px solid rgba(10,10,15,0.12)',
      boxShadow: '0 12px 40px rgba(10,10,15,0.18)',
      color: '#374151'
    }
  },
  bannerText: { m: 0, flex: '1 1 auto', minWidth: '240px' },
  bannerActions: { display: 'flex', gap: '0.5rem', flexShrink: 0, ml: 'auto' },
  bannerBtn: {
    px: '0.75rem',
    py: '0.4rem',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
    flex: '0 0 auto'
  },
  title: { color: '#f5f6f8', fontWeight: 700, fontSize: '1rem', m: 0, mb: '0.5rem' },
  link: { color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer' },
  row: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem', mt: '1rem' },
  btn: {
    boxSizing: 'border-box',
    flex: '1 1 auto',
    border: '1px solid var(--edge)',
    borderRadius: '8px',
    background: 'transparent',
    color: '#f5f6f8 !important',
    '[data-theme="light"] &': {
      color: '#1a1a26 !important',
      border: '1px solid rgba(10,10,15,0.25)'
    },
    px: '0.9rem',
    py: '0.55rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': { borderColor: 'var(--primary)' }
  },
  btnPrimary: {
    background: 'var(--cta-btn-bg)',
    color: 'var(--cta-btn-text) !important',
    border: '1px solid var(--cta-btn-bg)',
    '[data-theme="light"] &': {
      color: 'var(--cta-btn-text) !important',
      border: '1px solid var(--cta-btn-bg)'
    },
    '&:hover': { background: 'var(--cta-btn-hover)', borderColor: 'var(--cta-btn-hover)', filter: 'none' }
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 10000,
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: '1rem'
  },
  dialog: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '560px',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: '#14141d',
    border: '1px solid rgba(255,255,255,0.14)',
    borderRadius: '14px',
    p: ['1.25rem', '1.75rem'],
    color: '#d4d6dd',
    fontSize: '0.9rem',
    lineHeight: 1.6,
    '& strong': { color: '#f5f6f8' },
    '[data-theme="light"] &': {
      background: '#ffffff',
      border: '1px solid rgba(10,10,15,0.12)',
      color: '#374151',
      '& strong': { color: '#1a1a26' }
    }
  },
  cat: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    py: '0.9rem',
    borderTop: '1px solid var(--edge)'
  },
  toggle: {
    appearance: 'none',
    width: '42px',
    height: '24px',
    borderRadius: '12px',
    background: 'var(--black5)',
    border: '1px solid var(--edge)',
    position: 'relative',
    cursor: 'pointer',
    flexShrink: 0,
    mt: '2px',
    '&:checked': { background: 'var(--primary)' },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: '3px',
      left: '3px',
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      background: 'var(--white)',
      transition: 'transform 0.15s'
    },
    '&:checked::after': { transform: 'translateX(18px)' },
    '&:disabled': { opacity: 0.6, cursor: 'not-allowed' }
  }
}

const CookieConsent = () => {
  const [ready, setReady] = useState(false)
  const [bannerOpen, setBannerOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [choices, setChoices] = useState(ALL_OFF)

  const openDialog = useCallback(() => {
    const stored = readStored()
    setChoices(stored ? stored.choices : ALL_OFF)
    setDialogOpen(true)
  }, [])

  useEffect(() => {
    const stored = readStored()
    if (stored) {
      applyConsent(stored.choices)
      setChoices(stored.choices)
    } else {
      setBannerOpen(true)
    }
    setReady(true)

    const onSettings = () => openDialog()
    const onHash = () => {
      if (window.location.hash === '#cookie-settings') openDialog()
    }
    window.addEventListener('sparkey:cookie-settings', onSettings)
    window.addEventListener('hashchange', onHash)
    onHash()
    return () => {
      window.removeEventListener('sparkey:cookie-settings', onSettings)
      window.removeEventListener('hashchange', onHash)
    }
  }, [openDialog])

  const commit = next => {
    writeStored(next)
    applyConsent(next)
    setChoices(next)
    setBannerOpen(false)
    setDialogOpen(false)
    if (window.location.hash === '#cookie-settings') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }

  if (!ready) return null

  return (
    <>
      {bannerOpen && !dialogOpen && (
        <div sx={styles.banner} role='dialog' aria-live='polite' aria-label='Cookie consent'>
          <p sx={styles.bannerText}>
            We use cookies — strictly necessary ones always, analytics only with your consent.{' '}
            See our{' '}
            <a href='/cookies' sx={styles.link}>
              Cookie Notice
            </a>
            .
          </p>
          <div sx={styles.bannerActions}>
            <button type='button' sx={{ ...styles.btn, ...styles.bannerBtn }} onClick={() => commit(ALL_OFF)}>
              Reject
            </button>
            <button type='button' sx={{ ...styles.btn, ...styles.bannerBtn }} onClick={openDialog}>
              Preferences
            </button>
            <button
              type='button'
              sx={{ ...styles.btn, ...styles.bannerBtn, ...styles.btnPrimary }}
              onClick={() => commit(ALL_ON)}
            >
              Accept all
            </button>
          </div>
        </div>
      )}

      {dialogOpen && (
        <div sx={styles.overlay} onClick={() => setDialogOpen(false)}>
          <div
            sx={styles.dialog}
            role='dialog'
            aria-modal='true'
            aria-label='Cookie preferences'
            onClick={e => e.stopPropagation()}
          >
            <p sx={styles.title}>Cookie preferences</p>
            <p sx={{ m: 0, mb: '0.75rem' }}>
              Choose which cookies sparkey.ai may set. You can change this at any time from the
              “Cookie settings” link in the footer. Details are in our{' '}
              <a href='/cookies' sx={styles.link}>
                Cookie Notice
              </a>
              .
            </p>
            {CATEGORIES.map(cat => (
              <div key={cat.id} sx={styles.cat}>
                <input
                  type='checkbox'
                  id={`cookie-${cat.id}`}
                  sx={styles.toggle}
                  checked={!!choices[cat.id]}
                  disabled={cat.locked}
                  onChange={e => setChoices({ ...choices, [cat.id]: e.target.checked })}
                />
                <label htmlFor={`cookie-${cat.id}`} sx={{ cursor: cat.locked ? 'default' : 'pointer' }}>
                  <strong>{cat.label}</strong>
                  {cat.locked && <span sx={{ color: '#9094a8', ml: '0.5rem', fontSize: '0.8rem' }}>Always on</span>}
                  <div sx={{ color: '#9094a8', fontSize: '0.85rem' }}>{cat.description}</div>
                </label>
              </div>
            ))}
            <div sx={styles.row}>
              <button type='button' sx={styles.btn} onClick={() => commit(ALL_OFF)}>
                Reject non-essential
              </button>
              <button type='button' sx={styles.btn} onClick={() => commit({ ...choices, necessary: true })}>
                Save preferences
              </button>
              <button
                type='button'
                sx={{ ...styles.btn, ...styles.btnPrimary }}
                onClick={() => commit(ALL_ON)}
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CookieConsent
